/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React from 'react';
import { jsx, cssMap } from '@compiled/react';
import { useIntl } from 'react-intl';

import Avatar from '@atlaskit/avatar/avatar';
import { aiSuggestionsMessages } from '@atlaskit/editor-common/ai-messages/ai-suggestions';
import { RovoHexIcon } from '@atlaskit/logo/rovo-hex/icon';
import {
	AGENT_AVATAR_CLIP_PATH,
	AgentAvatar,
} from '@atlaskit/rovo-agent-components/ui/AgentAvatar';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip/Tooltip';

import type { Suggestion } from '@atlassian/editor-plugin-ai-suggestions';

export type SuggestionAgentIdentityProps = {
	agent?: Suggestion['createdByAgent'];
	user?: Suggestion['createdByUser'];
};

const styles = cssMap({
	row: {
		display: 'inline-flex',
		alignItems: 'center',
		height: token('space.300'),
		isolation: 'isolate',
	},
	agentAvatar: {
		alignItems: 'center',
		display: 'inline-flex',
		flexShrink: 0,
		height: token('space.300'),
		justifyContent: 'center',
		position: 'relative',
		width: token('space.300'),
		zIndex: 2,
	},
	agentAvatarBorder: {
		backgroundColor: token('color.border.inverse'),
		inset: `calc(-1 * ${token('space.025')})`,
		position: 'absolute',
		zIndex: 0,
	},
	agentAvatarContent: {
		display: 'inline-flex',
		position: 'relative',
		zIndex: 1,
	},
	agentAvatarOverlapping: {
		marginInlineEnd: `calc(-1 * ${token('space.050')})`,
	},
	userAvatar: {
		display: 'inline-flex',
		position: 'relative',
		zIndex: 1,
	},
});

/** Renders the compact Rovo identity used when no agent avatar is available. */
function RovoIdentity(): React.JSX.Element {
	const { formatMessage } = useIntl();

	return (
		<RovoHexIcon label={formatMessage(aiSuggestionsMessages.cardRovoAuthorLabel)} size="small" />
	);
}

function AuthorsTooltip({
	agentName,
	children,
	userName,
}: {
	agentName: string;
	children: React.ReactElement;
	userName?: string;
}): React.JSX.Element {
	const { formatMessage } = useIntl();

	return (
		<Tooltip
			content={
				userName
					? formatMessage(aiSuggestionsMessages.cardAuthorsTooltipLabel, { agentName, userName })
					: agentName
			}
			ignoreTooltipPointerEvents
			position="bottom"
		>
			{children}
		</Tooltip>
	);
}

/** Renders the agent and user identities associated with a suggestion. */
export function SuggestionAgentIdentity({
	agent,
	user,
}: SuggestionAgentIdentityProps): React.JSX.Element {
	const { formatMessage } = useIntl();
	const agentName = agent?.name?.trim();
	const userName = user?.name?.trim();
	const validAgent =
		agent?.accountId && agentName
			? {
					accountId: agent.accountId,
					name: agentName,
					picture: agent.picture?.trim() || undefined,
				}
			: undefined;
	const validUser =
		user?.accountId && userName
			? {
					name: userName,
					picture: user.picture?.trim() || undefined,
				}
			: undefined;

	const authorAvatars = (
		<div css={styles.row}>
			<span css={[styles.agentAvatar, validUser && styles.agentAvatarOverlapping]}>
				<span
					aria-hidden
					css={styles.agentAvatarBorder}
					style={{
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/enforce-style-prop
						clipPath: AGENT_AVATAR_CLIP_PATH,
					}}
				/>
				<span css={styles.agentAvatarContent}>
					{validAgent ? (
						<AgentAvatar
							imageUrl={validAgent.picture}
							agentIdentityAccountId={validAgent.accountId}
							size="small"
							label={validAgent.name}
							showBorder={false}
						/>
					) : (
						<RovoIdentity />
					)}
				</span>
			</span>
			{validUser && (
				<span css={styles.userAvatar}>
					<Avatar
						label={validUser.name}
						name={validUser.name}
						size="small"
						src={validUser.picture}
					/>
				</span>
			)}
		</div>
	);

	return (
		<AuthorsTooltip
			agentName={validAgent?.name ?? formatMessage(aiSuggestionsMessages.cardRovoAuthorLabel)}
			userName={validUser?.name}
		>
			{authorAvatars}
		</AuthorsTooltip>
	);
}
